import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import { Container, Box, Typography, Grid, Dialog, DialogContent, useMediaQuery, Alert, useTheme, Snackbar, IconButton, Paper, Fade, Collapse, Chip, Card, CardContent, Avatar, Divider, CircularProgress, TextField, InputAdornment, Popover, Stack, Button, Tooltip } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { DataGrid } from "@mui/x-data-grid";
import { HiOutlineClock, HiOutlineCalendar, HiOutlineXCircle, HiOutlineCheckCircle } from "react-icons/hi";
import { FaHourglassHalf, FaCalendarAlt } from "react-icons/fa";
import HistoryIcon from "@mui/icons-material/History";
import SearchIcon from "@mui/icons-material/Search";
import InquiryForm from "./InquiryForm";
import HistoryManagement from "./HistoryManagement";
import { useNavigate } from "react-router-dom";
import { FaUser } from "react-icons/fa";
import { MdLocationPin, MdPerson, MdLink, MdPhone, MdCurrencyRupee, MdAssignmentInd, MdCalendarToday } from "react-icons/md";
import { useAuth } from "../context/AuthContext";
import FilterListIcon from "@mui/icons-material/FilterList";
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import FollowUpFilterModal from "../components/FollowUpFilterModal";
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import { FaFilter } from "react-icons/fa";
import { FcFilledFilter } from "react-icons/fc";
import CustomNoDataOverlay from "../components/CustomNoDataOverlay";
import NoDataComponent from "../components/NoDataComponent";
import { FiFilter } from "react-icons/fi";
import { motion, AnimatePresence, config } from "framer-motion";
import { darken } from "@mui/material/styles";
import { FaArrowRight } from "react-icons/fa";
import { formatRows } from "../utils/formatRows";
import CheckIcon from "@mui/icons-material/Check";
import { FaCheckDouble } from "react-icons/fa";
import { FaCheck } from "react-icons/fa";
import { minWidth } from "@mui/system";
import { CSVLink } from "react-csv";
import { FaFileExcel } from "react-icons/fa";

/**
 * FollowUpPageV3 Component
 * 
 * Displays inquiry follow-ups categorized by:
 * - Missed: Yesterday and before
 * - Today: Today's follow-ups
 * - Upcomming: Next 7 days
 * - Delayed: Beyond 7 days
 * - Failed: Failed status
 * 
 * Features:
 * - Server-side search and filtering
 * - Category-based data organization
 * - Team Leader support (splits self vs team inquiries)
 * - User hierarchy support
 * - Mobile + Desktop views
 */

const CATEGORIES = [
    { key: "Today", label: "Today" },
    { key: "Tomorrow", label: "Tomorrow" },
    { key: "NextWeek", label: "Next Week" }, // display text
    { key: "Missed", label: "Missed" },
    { key: "Delayed", label: "Delayed" },
    { key: "Failed", label: "Failed" },
    { key: "Success", label: "Success" },
];

const ALL_FILTERED_CATEGORY = {
    key: "ALL_FILTERED",
    label: "All"
};

const CATEGORY_CONFIG = {

    Today: {
        color: "#ff9a47ff",
        icon: <HiOutlineCalendar size={18} color="white" />,
    },
    Tomorrow: {
        color: "#20c3b3ff",
        icon: <FaArrowRight size={15} color="white" />,
    },
    NextWeek: {
        color: "#37b8efff",
        icon: <FaCalendarAlt size={15} color="white" />,
    },
    Missed: {
        color: "#FACC15",
        icon: <HiOutlineClock size={18} color="white" />,
    },

    Delayed: {
        color: "#A78BFA",
        icon: <FaHourglassHalf size={15} color="white" />,
    },
    Failed: {
        color: "#EF4444",
        icon: <HiOutlineXCircle size={18} color="white" />,
    },

    Success: {
        color: "#22C55E",
        icon: <HiOutlineCheckCircle size={18} color="white" />,
    },

    ALL_FILTERED: {
        color: "#2563EB",
        icon: <FaFilter size={16} color="white" />,
    }
};



const splitRecordsForTeamLeader = (records, userId) => ({
    self: records.filter(r => r.UserId === userId),
    team: records.filter(r => r.UserId !== userId)
});

const stringToColor = (string) => {
    let hash = 0;
    for (let i = 0; i < string.length; i++) hash = string.charCodeAt(i) + ((hash << 5) - hash);
    const colors = ["#e74c3c", "#3498db", "#2ecc71", "#e67e22", "#9b59b6", "#1abc9c", "#3f51b5", "#e91e63", "#795548", "#00bcd4"];
    return colors[Math.abs(hash) % colors.length];
};

const getInitials = (name) => name?.trim().charAt(0).toUpperCase() || "C";

const getPriorityColor = (priority) => {
    switch ((priority || "").toLowerCase()) {
        case "high": return "#E74C3C";
        case "mid": return "#F39C12";
        case "low": return "#2ECC71";
        default: return "#888";
    }
};

const formatDate = (iso) => {
    if (!iso) return "N/A";
    const d = new Date(iso);
    return `${d.getDate().toString().padStart(2, "0")}-${(d.getMonth() + 1).toString().padStart(2, "0")}-${d.getFullYear()}`;
};

// const getStatusChipStyle = (status) => {
//     const s = status?.toLowerCase();
//     if (s === "success") return { backgroundColor: "#4caf50", color: "#fff" };
//     if (s === "failed") return { backgroundColor: "#d32f2f", color: "#fff" };
//     return { backgroundColor: "#FF4C00", color: "#fff" };
// };


const getStatusChipStyle = (status, statusColor) => {
    if (statusColor) {
        return { backgroundColor: statusColor, color: "#fff" };
    }
    const s = status?.toLowerCase();
    if (s === "success") return { backgroundColor: "#4caf50", color: "#fff" };
    if (s === "failed") return { backgroundColor: "#d32f2f", color: "#fff" };
    return { backgroundColor: "#FF4C00", color: "#fff" };
};

export default function FollowUpPageV3({ connectionString }) {
    const { user, selectedCompany } = useAuth();
    const API = `${connectionString}/api`;
    const navigate = useNavigate();
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));

    // ============ STATE MANAGEMENT ============

    const [dataSets, setDataSets] = useState({});
    const [visibleCategory, setVisibleCategory] = useState("Today");
    const [recordCounts, setRecordCounts] = useState({});
    const [categoryLoading, setCategoryLoading] = useState({});
    const [showEnquiryModal, setShowEnquiryModal] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [showHistoryModal, setShowHistoryModal] = useState(false);
    const [historyInquiryId, setHistoryInquiryId] = useState(null);
    const [activeFilters, setActiveFilters] = useState(null);
    // const [isFilterApplied, setIsFilterApplied] = useState(false);
    // const [filteredData, setFilteredData] = useState([]);
    const [filterLoading, setFilterLoading] = useState(false);
    const [filteredRows, setFilteredRows] = useState([]);
    const [showFilteredGrid, setShowFilteredGrid] = useState(false);
    const [viewMode, setViewMode] = useState("category");

    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "" });

    // Search state (per category)
    const [searchQueries, setSearchQueries] = useState({});
    const [debouncedSearches, setDebouncedSearches] = useState({});
    const lastFetchRef = useRef("");

    const userId = user?.UserId;
    const companyId = selectedCompany?.CompId;
    const role = user?.UserCategory;

    console.log("=== FOLLOW-UP PAGE RENDER ===");
    console.log("visibleCategory:", visibleCategory);
    console.log("recordCounts:", recordCounts);

    //state for filter modal
    const [filterAnchorEl, setFilterAnchorEl] = useState(null);
    const openFilter = Boolean(filterAnchorEl);

    const handleFilterOpen = (event) => {
        // setIsFilterApplied(true);    // 👈 re-enter filter mode
        // setVisibleCategory(null);    // 👈 hide category view
        setFilterAnchorEl(event.currentTarget);
    };

    const fetchFilteredGridData = async (filters) => {
        try {
            const params = {
                userId,
                companyId,
                role,
                offset: 0,
                limit: 500, // or pagination later
            };

            if (filters.fromDate) params.fromDate = filters.fromDate;
            if (filters.toDate) params.toDate = filters.toDate;
            if (filters.selectedStatus) params.statusId = filters.selectedStatus;
            if (filters.selectedUser) params.addUserId = filters.selectedUser;
            if (filters.selectedSource) params.sourceId = filters.selectedSource;
            if (filters.selectedAssignee) params.assigneeId = filters.selectedAssignee;
            if (filters.selectedClient) params.clientId = filters.selectedClient;
            if (filters.selectedPriority) params.priorityId = filters.selectedPriority;
            if (filters.area) params.area = filters.area;
            if (filters.dateType) params.dateType = filters.dateType;

            const { data } = await axios.get(`${API}/filter-enquiries`, {
                params,
                headers: { "Cache-Control": "no-store" }
            });

            // const enquiries = data.enquiries || [];
            // const normalized =
            //     role === "Team Leader"
            //         ? [...(enquiries.self || []), ...(enquiries.team || [])]
            //         : enquiries;

            // setFilteredRows(formatRows(normalized));
            const enquiries = data.enquiries || [];
            console.log("enq:", enquiries);

            let normalized = [];

            if (Array.isArray(enquiries)) {
                normalized = enquiries;
            } else if (enquiries.self || enquiries.team) {
                normalized = [...(enquiries.self || []), ...(enquiries.team || [])];
            }

            setFilteredRows(formatRows(normalized));

            setShowFilteredGrid(true);

        } catch (err) {
            console.error("Filtered grid fetch failed", err);
            setFilteredRows([]);
        }
    };



    const handleFilterClose = () => {
        setFilterAnchorEl(null);
    };

    const NoFilterResultMessage = (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                paddingTop: '30px',
                minHeight: '60vh',
                textAlign: 'center',
                gap: 2
            }}
        >
            <Box sx={{ fontSize: 80, opacity: 0.3 }}>🔍</Box>
            <Typography variant="h5" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                No Results Found
            </Typography>
            <Typography variant="h6" sx={{ color: 'text.disabled', maxWidth: 500 }}>
                Try changing or clearing the filters to see enquiries
            </Typography>
        </Box>
    );

    const updateCountsFromDataSets = (dataSets) => {
        const newCounts = {};

        CATEGORIES.forEach(({ key }) => {
            const data = dataSets[key];

            if (!data) {
                newCounts[key] = 0;
                return;
            }

            if (Array.isArray(data)) {
                newCounts[key] = data.length;
            } else {
                newCounts[key] =
                    (data.self?.length || 0) + (data.team?.length || 0);
            }
        });

        setRecordCounts(newCounts);
    };

    const updateCountsFromFilteredData = async (filters) => {
        const counts = {};

        for (const { key } of CATEGORIES) {
            const data = await fetchEnquiries(key, "", filters);

            if (Array.isArray(data)) {
                counts[key] = data.length;
            } else {
                counts[key] = (data.self?.length || 0) + (data.team?.length || 0);
            }
        }

        setRecordCounts(counts);
    };




    /**
 * Handle filter apply
 */
    // const handleApplyFilter = async (filterData) => {
    //     console.log("🔍 [FILTER] Applying filters:", filterData);

    //     setActiveFilters(filterData);
    //     setIsFilterApplied(true);
    //     handleFilterClose();

    //     // Fetch filtered data
    //     const newData = await fetchFilteredEnquiries(filterData);
    //     setFilteredData(newData);

    //     // Clear visible category since we're now in filter mode
    //     setVisibleCategory(null);
    // };

    // const handleApplyFilter = async (filterData) => {
    //     console.log("🔍 [FILTER] Applying filters:", filterData);

    //     setActiveFilters(filterData);
    //     handleFilterClose();

    //     // Refetch current visible category with new filters
    //     if (visibleCategory) {
    //         const search = debouncedSearches[visibleCategory] || "";
    //         const newData = await fetchEnquiries(visibleCategory, search, filterData);
    //         setDataSets(prev => ({ ...prev, [visibleCategory]: newData }));
    //     }
    // };

    const handleApplyFilter = async (filterData) => {
        console.log("🔍 [FILTER] Applying filters:", filterData);

        setActiveFilters(filterData);
        handleFilterClose();
        setViewMode("filter");     // 👈 enter filter mode
        setVisibleCategory(null);

        if (visibleCategory) {
            const search = debouncedSearches[visibleCategory] || "";
            const newData = await fetchEnquiries(visibleCategory, search, filterData);

            setDataSets(prev => ({
                ...prev,
                [visibleCategory]: newData
            }));
        }

        // ✅ PLACE IT HERE (IMPORTANT)
        await fetchFilteredGridData(filterData);
        await updateCountsFromFilteredData(filterData);
    };



    /**
     * Handle filter clear
     */
    const handleClearFilter = async () => {
        setActiveFilters(null);
        handleFilterClose();
        setFilteredRows([]);
        setShowFilteredGrid(false);
        setVisibleCategory("Today");

        // Refetch current category without filters
        if (visibleCategory) {
            const search = debouncedSearches[visibleCategory] || "";
            const newData = await fetchEnquiries(visibleCategory, search, null);
            setDataSets(prev => ({ ...prev, [visibleCategory]: newData }));
        }

        await fetchCounts();
    };

    const fetchEnquiries = useCallback(async (category, searchText = "", appliedFilters = null) => {
        setCategoryLoading(prev => ({ ...prev, [category]: true }));

        try {
            // If filters are applied, use filter endpoint
            if (appliedFilters) {
                const params = {
                    category,  // Add category to filter params
                    userId,
                    companyId,
                    role,
                    offset: 0,
                    limit: 100
                };

                // Add filter params
                if (appliedFilters.fromDate) params.fromDate = appliedFilters.fromDate;
                if (appliedFilters.toDate) params.toDate = appliedFilters.toDate;
                if (appliedFilters.selectedStatus) params.statusId = appliedFilters.selectedStatus;
                if (appliedFilters.selectedUser) params.addUserId = appliedFilters.selectedUser;
                if (appliedFilters.selectedSource) params.sourceId = appliedFilters.selectedSource;
                if (appliedFilters.selectedAssignee) params.assigneeId = appliedFilters.selectedAssignee;
                if (appliedFilters.selectedClient) params.clientId = appliedFilters.selectedClient;
                if (appliedFilters.selectedPriority) params.priorityId = appliedFilters.selectedPriority;
                if (appliedFilters.area) params.area = appliedFilters.area;
                if (searchText && searchText.trim()) params.search = searchText;
                if (appliedFilters.dateType) params.dateType = appliedFilters.dateType;

                const { data } = await axios.get(`${API}/filter-enquiries`, {
                    params,
                    headers: { "Cache-Control": "no-store" }
                });
                const enquiries = data.enquiries || [];
                return role === "Team Leader" ? splitRecordsForTeamLeader(enquiries, userId) : enquiries;
            }

            // Otherwise use normal category endpoint
            const params = { category, userId, companyId, role, offset: 0, limit: 100 };
            if (searchText && searchText.trim()) {
                params.search = searchText;
            }

            const { data } = await axios.get(`${API}/get-enquiries`, {
                params,
                headers: { "Cache-Control": "no-store" }
            });

            const enquiries = data.enquiries || [];
            return role === "Team Leader" ? splitRecordsForTeamLeader(enquiries, userId) : enquiries;

        } catch (err) {
            console.error(`❌ Fetch error for ${category}:`, err);
            return [];
        } finally {
            setCategoryLoading(prev => ({ ...prev, [category]: false }));
        }
    }, [userId, companyId, role, API]);

    /**
     * Fetch count for all categories
     */
    const fetchCounts = useCallback(async () => {
        try {
            const { data: cnt } = await axios.get(`${API}/get-counts`, { params: { userId, role, companyId }, headers: { "Cache-Control": "no-store" } });

            if (cnt["Up Comming"] !== undefined) {
                cnt["Upcomming"] = cnt["Up Comming"];
                delete cnt["Up Comming"];
            }

            setRecordCounts(cnt || {});
        } catch (err) {
            console.error("❌ Count fetch error:", err);
        }
    }, [userId, role, companyId]);

    /**
     * Refresh all data
     */
    const refreshAll = useCallback(async () => {
        await fetchCounts();

        // Refresh visible category if any, WITH current filters
        if (visibleCategory) {
            const search = debouncedSearches[visibleCategory] || "";
            const newData = await fetchEnquiries(visibleCategory, search, activeFilters);
            setDataSets(prev => ({ ...prev, [visibleCategory]: newData }));
        }
    }, [fetchCounts, fetchEnquiries, visibleCategory, debouncedSearches, activeFilters]);

    // ============ EFFECTS ============

    /**
     * Initial load
     */
    useEffect(() => {
        (async () => {
            await fetchCounts();
        })();
    }, [fetchCounts]);

    /**
     * Load data when category is selected
     */
   
    useEffect(() => {
        (async () => {
            if (!visibleCategory) return;

            const search = debouncedSearches[visibleCategory] || "";
            const fetchKey = `${visibleCategory}__${search}__${JSON.stringify(activeFilters)}`;

            if (lastFetchRef.current === fetchKey) return;
            lastFetchRef.current = fetchKey;

            // Pass activeFilters to fetchEnquiries
            const newData = await fetchEnquiries(visibleCategory, search, activeFilters);
            setDataSets(prev => ({ ...prev, [visibleCategory]: newData }));
        })();
    }, [visibleCategory, debouncedSearches, activeFilters, fetchEnquiries]);


    /**
     * Debounce search for visible category
     */
    useEffect(() => {
        if (!visibleCategory) return;

        console.log(`⏱️ [DEBOUNCE] Starting 400ms timeout for: ${visibleCategory}`);

        const timer = setTimeout(() => {
            console.log(`⏱️ [DEBOUNCE] Timer completed for: ${visibleCategory}`);
            setDebouncedSearches(prev => ({ ...prev, [visibleCategory]: searchQueries[visibleCategory] || "" }));
        }, 400);

        return () => {
            console.log(`⏱️ [DEBOUNCE] Cleared timeout for: ${visibleCategory}`);
            clearTimeout(timer);
        };
    }, [visibleCategory, searchQueries]);

    // ============ HANDLERS ============
    const toggleCategory = (cat) => {
        if (cat === "ALL_FILTERED") {
            setViewMode("filter");
            setShowFilteredGrid(true);
            setVisibleCategory(null);
            return;
        }

        setViewMode("category");
        setVisibleCategory(prev => (prev === cat ? null : cat));
    };


    console.log("visibleCategory", visibleCategory)

    const handleRowClick = (row) => {
        console.log(`✏️ [EDIT] InqId: ${row.InqId}`);
        setSelectedRecord(row);
        setShowEnquiryModal(true);
    };

    const handleSearchChange = (cat, value) => {
        console.log(`🔍 [SEARCH] ${cat}: "${value}"`);
        setSearchQueries(prev => ({ ...prev, [cat]: value }));
    };

    const handlePopupSuccess = (msg) => {
        setSnackbar({ open: true, message: msg, severity: "success" });
        setShowEnquiryModal(false);
    };



    // ============ DATA PREPARATION ============
    const rows = useMemo(() => {
        if (!visibleCategory) return [];

        const data = dataSets[visibleCategory];
        if (!data) return [];

        let arrayData = Array.isArray(data)
            ? data
            : (data.self || data.team)
                ? [...(data.self || []), ...(data.team || [])]
                : [];

        return formatRows(arrayData);
    }, [dataSets, visibleCategory, role]);

    const exportRows = useMemo(() => {
        if (viewMode === "filter") {
            return filteredRows;
        }
        if (viewMode === "category" && visibleCategory) {
            return rows;
        }
        return [];
    }, [viewMode, filteredRows, rows, visibleCategory]);

    // ============ COLUMNS DEFINITION ============

    const columns = [
        {
            field: "InqNo", headerName: "Inquiry No", flex: 0.5, minWidth: 120,
            renderCell: ({ row }) => (
                <Box>
                    <Typography variant="subtitle2">{row.InqNo || "-"}</Typography>
                    <Typography variant="body2" color="text.secondary">
                        {row.InqDate ? new Date(row.InqDate).toLocaleDateString("en-GB") : "-"}
                    </Typography>
                </Box>
            )
        },
        {
            field: "FirmName", headerName: "Client Info", flex: 2, minWidth: 180, sortable: false, filterable: true,
            renderCell: ({ row }) => (
                <Box sx={{ cursor: "pointer", my: 1 }}
                    onClick={(e) => {
                        e.stopPropagation();
                        handleRowClick(row);
                    }}
                >
                    <Typography variant="subtitle2" sx={{ color: "#1877F2", textDecoration: "underline", fontSize: "0.85rem" }}> {row.FirmName} </Typography>
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <FaUser color="#FF9800" />
                        <Typography variant="body2">{row.ContactPerson || "-"}</Typography>
                    </Stack>
                    <Typography variant="body2" color="text.secondary">📲 {row.MobileNo || "-"}</Typography>
                    {row.EmailId && <Typography variant="body2" color="text.secondary">📧 {row.EmailId}</Typography>}
                    {row.city && (
                        <Tooltip
                            componentsProps={{
                                tooltip: {
                                    sx: {
                                        fontSize: "15px",
                                        lineHeight: "22px",
                                    }
                                }
                            }}
                            title={
                                `${row.Address || ""} ${row.Area || ""}, ${row.city || ""}, ${row.state || ""}-${row.Pincode || ""}`
                            }
                            placement="right"
                            arrow
                        >
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                display="flex"
                                alignItems="center"
                                gap={0.6}
                                sx={{ cursor: "help" }}
                            >
                                <MdLocationPin style={{ color: "#1877F2" }} />
                                {row.Area}{row.city ? `, ${row.city}` : ""}
                            </Typography>
                        </Tooltip>
                    )}

                </Box>
            )
        },
        {
            field: "Next Follow-Up Date", headerName: "Next Follow-Up Date", flex: 0.7, minWidth: 60,
            renderCell: ({ row }) => (
                <Box>
                    <Typography variant="body2" textAlign='center'> {row.LastFollowUpDate ? new Date(row.LastFollowUpDate).toLocaleDateString("en-GB") : "-"} </Typography>
                </Box>
            )
        },
        {
            field: "OfferPrice", headerName: "Prices", flex: 0.7, minWidth: 110,
            renderCell: ({ row }) => (
                <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}> Offer: ₹{row.OfferPrice || "0.00"} </Typography>
                    <Typography variant="body2" color="text.secondary"> Closing: ₹{row.ClosingPrice || "0.00"} </Typography>
                </Box>
            )
        },
        { field: "LaststatusName", headerName: "Status", flex: 0.5, minWidth: 100, renderCell: ({ row, value }) => (<Chip label={value || "—"} size="small" sx={{ fontWeight: 600, ...getStatusChipStyle(value, row.StatusColor) }} />) },
        {
            field: "LeadFrom",
            headerName: "Lead From",
            flex: 0.4,
            minWidth: 80,
            renderCell: ({ row }) => (
                <Typography variant="body2" sx={{ whiteSpace: "normal", wordBreak: "break-word" }}>{row.LeadFrom || "-"}</Typography>
            )
        },
        {
            field: "EmployeeName",
            headerName: "Lead To",
            flex: 0.4,
            minWidth: 80,
            renderCell: ({ row }) => (
                <Typography variant="body2" sx={{ whiteSpace: "normal", wordBreak: "break-word" }}>{row.EmployeeName || "-"}</Typography>
            )
        },
        { field: "LastleadSourceName", headerName: "Source", flex: 0.5, minWidth: 80 },
        { field: "Lastpriority", headerName: "Priority", flex: 0.3, minWidth: 70 },
        {
            field: "history", headerName: "History", sortable: false, flex: 0.3, align: "center", minWidth: 40,
            renderCell: ({ row }) => (
                <IconButton color="primary"
                    onClick={() => {
                        setHistoryInquiryId(row.InqId);
                        setShowHistoryModal(true);
                    }}
                >
                    <HistoryIcon />
                </IconButton>
            )
        }
    ];

    // ============ CARD RENDERING (MOBILE) ============

    const renderCards = () => {
        if (categoryLoading?.[visibleCategory]) {
            return (
                <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 4 }}>
                    <CircularProgress />
                </Box>
            );
        }

        if (!rows.length) {
            return (
                <Typography sx={{ p: 2, textAlign: "center", color: "text.secondary" }}>
                    No records found.
                </Typography>
            );
        }

        return rows.map((row) => {
            const avatarColor = stringToColor(row.FirmName || "Inquiry");
            const initials = getInitials(row.FirmName);
            const priorityColor = getPriorityColor(row.Lastpriority);

            return (
                <Card key={row.InqId} onClick={() => handleRowClick(row)} sx={{ mb: 2, borderLeft: `6px solid ${priorityColor}`, borderRadius: 2, boxShadow: 2, cursor: "pointer", "&:hover": { boxShadow: 4 } }} >
                    <CardContent sx={{ p: 2 }}>
                        <Grid container alignItems="center" justifyContent="space-between">
                            <Grid item>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                                    <Avatar sx={{ bgcolor: avatarColor, color: '#fff', width: 36, height: 36 }}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                                            {initials}
                                        </Typography>
                                    </Avatar>
                                    <Box>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                            {row.FirmName || "—"}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {row.city || "—"}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Grid>
                            <Grid item>
                                <Box sx={{ bgcolor: row.StatusColor || priorityColor, px: 1.5, py: 0.5, borderRadius: 3 }}>
                                    <Typography variant="caption" sx={{ color: "white", fontWeight: 600 }}>
                                        {row.LaststatusName || "—"} | {(row.Lastpriority || "")[0] || "?"}
                                    </Typography>
                                </Box>
                            </Grid>
                        </Grid>

                        <Grid container justifyContent="space-between" sx={{ mt: 2 }}>
                            <Grid item>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    <MdPerson fontSize={18} color="gray" />
                                    <Typography variant="body2" color="text.secondary"> {row.ContactPerson || "N/A"} </Typography>
                                </Box>
                            </Grid>
                            <Grid item>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    <MdLink fontSize={18} color="gray" />
                                    <Typography variant="body2" color="text.secondary"> {row.LastleadSourceName || "Unknown"} </Typography>
                                </Box>
                            </Grid>
                        </Grid>

                        <Grid container justifyContent="space-between" sx={{ mt: 1.5, mb: 2 }}>
                            <Grid item>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    <MdPhone fontSize={18} color={theme.palette.primary.main} />
                                    <Typography variant="body2" sx={{ color: theme.palette.primary.main, fontWeight: 500 }}> {row.MobileNo || "N/A"} </Typography>
                                </Box>
                            </Grid>
                            <Grid item>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    <MdCurrencyRupee fontSize={18} color="gray" />
                                    <Typography variant="body2" color="text.secondary"> {row.ClosingPrice || "0.00"} </Typography>
                                </Box>
                            </Grid>
                        </Grid>

                        <Divider sx={{ my: 1.5 }} />

                        <Grid container justifyContent="space-between" alignItems="center">
                            <Grid item>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    <MdAssignmentInd fontSize={18} color="gray" />
                                    <Typography variant="body2" color="text.secondary" sx={{ textTransform: "uppercase" }}>
                                        {row.EmployeeName || "Unknown"}
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    <MdCalendarToday fontSize={18} color="gray" />
                                    <Typography variant="body2" color="text.secondary">
                                        {formatDate(row.LastFollowUpDate)}
                                    </Typography>
                                </Box>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
            );
        });
    };

    // ============ RENDER ============

    return (
        <Box sx={{ pt: 0.7, px: { xs: 2, sm: 3, md: 4 } }}>

            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.7, }} >
                <Typography variant="h5" sx={{ textAlign: "center", fontSize: { xs: "1.25rem", sm: "1.5rem" }, fontWeight: 600, flex: 1, }} >
                    Inquiry Follow-Up
                </Typography>

                {exportRows.length > 0 && (
                    <CSVLink
                        data={exportRows}
                        filename={
                            viewMode === "filter"
                                ? "Filtered_Followups.csv"
                                : `${visibleCategory}_Followups.csv`
                        }
                        style={{ textDecoration: "none" }}
                    >
                        <Tooltip title="Export to Excel">
                            <IconButton
                                sx={{
                                    ml: 1,
                                    bgcolor: "#1D6F42",
                                    color: "#fff",
                                    "&:hover": { bgcolor: "#155D36" }
                                }}
                            >
                                <FaFileExcel size={18} />
                            </IconButton>
                        </Tooltip>
                    </CSVLink>
                )}

                {/* <Tooltip title="Filter Follow-Up">

                    <IconButton
                        onClick={handleFilterOpen}
                        sx={{
                            bgcolor: "#ebf5ffff",
                            "&:hover": { bgcolor: "#e2e8f0" },
                        }}
                    >
                       <FilterAltIcon sx={{ color: "primary.main", fontSize: 26 }} />
                    </IconButton>
                </Tooltip> */}
            </Box>

            {/* Category Buttons */}
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.2, justifyContent: isSmallScreen ? "center" : "center", my: 1, mt: 1 }} >
                {/* {CATEGORIES.map(({ key, label }) => { */}
                {[
                    ...CATEGORIES,
                    ...(activeFilters ? [ALL_FILTERED_CATEGORY] : [])
                ].map(({ key, label }) => {
                    const { color, icon } = CATEGORY_CONFIG[key];
                    const isSelected =
                        key === "ALL_FILTERED"
                            ? viewMode === "filter"
                            : visibleCategory === key;
                    const count =
                        key === "ALL_FILTERED"
                            ? filteredRows.length
                            : recordCounts[key] || 0;

                    return (
                        <Chip
                            key={key}
                            clickable
                            onClick={() => toggleCategory(key)}
                            sx={{
                                height: 45,
                                px: 1.8,
                                borderRadius: 3,
                                bgcolor: color,
                                color: "#fff",
                                border: isSelected
                                    ? `1px solid #fff`
                                    : `1px solid ${color}`,
                                outline: isSelected
                                    ? `2px solid ${color}`
                                    : 'none',
                                transition: "all 0.25s ease",
                                transform: isSelected ? "scale(1.05)" : "scale(1)",
                                boxShadow: isSelected
                                    ? "0 4px 12px rgba(0,0,0,0.2)"
                                    : "none",
                                "&:hover": {
                                    bgcolor: color,
                                    color: "#fff",
                                    transform: isSelected ? "scale(1.05)" : "translateY(-2px)",
                                },
                            }}
                            label={
                                <Box sx={{
                                    display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", py: 0.6
                                    // gap: 0.5,
                                }}>
                                    {/* First Line: Icon + Category Name */}
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
                                        <Box sx={{ display: "flex", alignItems: "center", color: "#fff", marginRight: '2px' }}>
                                            {icon}
                                        </Box>
                                        <Typography sx={{
                                            fontSize: 15, color: "#fff"
                                            // fontWeight: 600,
                                        }}>
                                            {label}
                                        </Typography>

                                        {isSelected && (
                                            // <Box sx={{ fontSize: 18, ml: 0.5 }}>✔</Box>
                                            <Box
                                                sx={{ ml: 0.5, bgcolor: "rgba(242, 242, 242, 1)", p: 0.2, color: "green", borderRadius: "50%", fontSize: '12px' }}
                                            >
                                                <FaCheck />
                                            </Box>
                                        )}
                                    </Box>

                                    {/* Second Line: Count */}
                                    <Typography sx={{ fontSize: 17, fontWeight: 500, color: "#fff", lineHeight: 1, }}>
                                        {count}
                                    </Typography>
                                </Box>
                            }
                        />
                    );
                })}
                <IconButton
                    onClick={handleFilterOpen}
                    sx={{
                        background: activeFilters ? 'linear-gradient(135deg, #00A1BC, #034EA2, #9D248F)' : '#ebf5ffff',
                        '&:hover': { background: activeFilters ? 'linear-gradient(135deg, #00A1BC, #034EA2, #9D248F)' : '#e2e8f0', },
                        color: activeFilters ? '#fff' : 'inherit',
                        position: 'relative',
                        transition: 'all 0.3s ease'
                    }}
                >
                    <FilterAltIcon sx={{ color: activeFilters ? "white" : "primary.main", fontSize: 26 }} />
                    {activeFilters && (
                        <Box sx={{ position: 'absolute', top: 5, right: 5, width: 8, height: 8, borderRadius: '50%', bgcolor: 'error.main' }} />
                    )}
                </IconButton>
            </Box>

            {viewMode === "filter" && showFilteredGrid && (
                <Paper elevation={3} sx={{ height: "68vh" }}>
                    <Typography sx={{ p: 2, fontWeight: 600 }}>
                        Filtered Results
                    </Typography>

                    <DataGrid
                        rows={filteredRows}
                        columns={columns}
                        getRowId={(r) => r.InqId}
                        onCellClick={(params) => params.field !== "history" && handleRowClick(params.row) }
                        getRowHeight={() => "auto"}
                        sx={{
                            '& .MuiDataGrid-columnHeaders': { backgroundColor: '#E0E0E0' },
                            '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 700 },
                            '& .MuiDataGrid-columnHeader': { backgroundColor: '#E0E0E0', color: '#000000', fontWeight: 'bold' },
                            '& .MuiDataGrid-row:nth-of-type(odd)': { backgroundColor: '#FFFFFF' },
                            '& .MuiDataGrid-row:nth-of-type(even)': { backgroundColor: '#F9FAFB' },
                            '& .MuiDataGrid-row:hover': { backgroundColor: '#f1f5f9' },
                            '& .MuiDataGrid-row.Mui-selected': { backgroundColor: '#BDBDBD !important' },
                            '& .MuiDataGrid-cell': { display: 'flex', alignItems: 'center' },
                        }}
                    />
                </Paper>
            )}

            {/* Category Content */}
            {viewMode === "category" && (
                !visibleCategory ? (
                    /* No category selected */
                    <Fade in={true} timeout={500}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: '30px', minHeight: '60vh', textAlign: 'center', gap: 2 }}>
                            <Box sx={{ fontSize: 80, opacity: 0.3 }}>📋</Box>
                            <Typography variant="h5" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                No Category Selected
                            </Typography>
                            <Typography variant="h6" sx={{ color: 'text.disabled', maxWidth: 500 }}>
                                Choose a category above to explore your enquiries 🔍
                            </Typography>
                        </Box>
                    </Fade>
                ) : (
                    /* Show category content */
                    CATEGORIES.map(({ key }) => {
                        const isEmpty = rows?.length === 0;
                        return (
                            <Fade in={visibleCategory === key} timeout={500} key={key} unmountOnExit>
                                <Collapse in={visibleCategory === key} timeout={700} unmountOnExit>
                                    <Paper elevation={3} sx={{ borderRadius: 2, overflow: "hidden", mt: 1 }}>
                                        <Box sx={{ p: { xs: 0.5, md: 0.5 } }}>
                                            {/* Mobile View */}
                                            {isSmallScreen ? (
                                                <Box sx={{ p: 2, maxHeight: '70vh', overflowY: 'auto' }}>
                                                    {rows?.length === 0 ? (
                                                        <NoDataComponent
                                                            title={activeFilters ? "No Results Found" : `No Inquiries found for ${visibleCategory}`}
                                                            subtitle={activeFilters ? "Try changing or clearing the filters" : ""}
                                                        />
                                                    ) : (
                                                        renderCards()
                                                    )}
                                                </Box>
                                            ) : (
                                                /* Desktop View */
                                                <Box sx={{ height: "73vh" }}>
                                                    {categoryLoading?.[key] ? (
                                                        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
                                                            <CircularProgress />
                                                        </Box>
                                                    ) : isEmpty ? (
                                                        <NoDataComponent title={activeFilters ? "No Results Found" : `No Inquiries found for ${visibleCategory} category`} subtitle={activeFilters ? "Try changing or clearing the filters to see enquiries" : ""} />
                                                    ) : (
                                                        <DataGrid
                                                            rows={rows}
                                                            columns={columns}
                                                            getRowId={(r) => r.InqId}
                                                            onCellClick={(params) => params.field !== "history" && handleRowClick(params.row)}
                                                            getRowHeight={() => "auto"}
                                                            sx={{
                                                                '& .MuiDataGrid-columnHeaders': { backgroundColor: '#E0E0E0' },
                                                                '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 700 },
                                                                '& .MuiDataGrid-columnHeader': { backgroundColor: '#E0E0E0', color: '#000000', fontWeight: 'bold' },
                                                                '& .MuiDataGrid-row:nth-of-type(odd)': { backgroundColor: '#FFFFFF' },
                                                                '& .MuiDataGrid-row:nth-of-type(even)': { backgroundColor: '#F9FAFB' },
                                                                '& .MuiDataGrid-row:hover': { backgroundColor: '#f1f5f9' },
                                                                '& .MuiDataGrid-row.Mui-selected': { backgroundColor: '#BDBDBD !important' },
                                                                '& .MuiDataGrid-cell': { display: 'flex', alignItems: 'center' },
                                                            }}
                                                            slots={{ noResultsOverlay: CustomNoDataOverlay }}
                                                        />
                                                    )}
                                                </Box>
                                            )}
                                        </Box>
                                    </Paper>
                                </Collapse>
                            </Fade>
                        );
                    })
                )
            )}

            <Snackbar
                open={snackbar.open}
                onClose={() => setSnackbar(s => ({ ...s, open: false }))}
                autoHideDuration={3000}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert variant="filled" severity={snackbar.severity}>
                    {snackbar.message}
                </Alert>
            </Snackbar>

            {/* Inquiry Modal */}
            <Dialog open={showEnquiryModal} fullWidth maxWidth="xl">
                <DialogContent>
                    <InquiryForm connectionString={connectionString} inquiryData={selectedRecord}
                        onSuccess={handlePopupSuccess}
                        onClose={() => {
                            setShowEnquiryModal(false);
                            refreshAll();
                        }}
                    />
                </DialogContent>
            </Dialog>

            {/* History Modal */}
            <HistoryManagement connectionString={connectionString} open={showHistoryModal} onClose={() => setShowHistoryModal(false)} inquiryId={historyInquiryId} maxWidth={isSmallScreen ? "sm" : "xl"} PaperProps={{ sx: { width: isSmallScreen ? "100%" : 900 } }} />

            {/* Filter Modal     */}
            <FollowUpFilterModal
                anchorEl={filterAnchorEl}
                open={openFilter}
                onClose={handleFilterClose}
                connectionString={connectionString}
                onClear={handleClearFilter}
                onApply={handleApplyFilter}
            />
        </Box>
    );
}